import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Stack, Title, Text, Anchor, Paper, List, Divider, Group, Badge, ThemeIcon, Table, SimpleGrid,
} from '@mantine/core';
import useAuthenticatedData from '../../hooks/useAuthenticatedData';
import DraftBadge from '../common/DraftBadge';
import config from '../../config';

// Long static reference for how sales commissions / bonuses work, written for business users
// (executives, sales managers, salespeople) — no code, plain language. Deep-linkable via URL hash
// (#anchor); each Section renders an element with id={...} and the page scrolls there on load.
// The two commission screens (Team Commissions, the rep bonus dashboard) link here.
//
// The payout SCHEDULES are fetched live from GET /sales/commission/payout-tables so the numbers here
// track the real config — change a payout row and the docs follow. DEFAULTS mirror the current seed
// config (commission_01_config_tables.sql) so the page renders instantly (and offline) before the fetch.
// The PLAN constants (targets / base bonus) are config that changes rarely and has no read endpoint,
// so they are stated statically here; if a plan's numbers change, update PLANS below to match the seed.

const COMP_YEAR = 2026;

// Mirrors the 'Revenue' rows of Scorecards.CommissionPayoutTable (threshold = % of plan → multiplier).
const REV_DEFAULT = [
  { thresholdMin: 0.50, payoutRate: 0.15 }, { thresholdMin: 0.75, payoutRate: 0.25 },
  { thresholdMin: 0.85, payoutRate: 0.50 }, { thresholdMin: 0.90, payoutRate: 0.80 },
  { thresholdMin: 0.95, payoutRate: 0.90 }, { thresholdMin: 0.975, payoutRate: 0.95 },
  { thresholdMin: 1.00, payoutRate: 1.00 }, { thresholdMin: 1.10, payoutRate: 1.10 },
  { thresholdMin: 1.20, payoutRate: 1.20 }, { thresholdMin: 1.30, payoutRate: 1.30 },
  { thresholdMin: 1.40, payoutRate: 1.40 }, { thresholdMin: 1.50, payoutRate: 1.50 },
];
// Mirrors the 'Variance' rows (threshold = decimal SvSP variance → multiplier). Below -5% = 0, in code.
const VAR_DEFAULT = [
  { thresholdMin: -0.05, payoutRate: 0.50 }, { thresholdMin: -0.02, payoutRate: 1.00 },
  { thresholdMin: 0.02, payoutRate: 1.10 }, { thresholdMin: 0.05, payoutRate: 1.20 },
];

// The two plans, from commission_01 seed + commission_04 rep mapping. bonusRate = AnnualTarget / BaseBonus.
const PLANS = [
  { name: 'Account Manager', target: 5000000, base: 25000, hold: 0.25 },
  { name: 'Account Rep',     target: 3500000, base: 10000, hold: 0.25 },
];
const BURDEN = 1.0765; // Scorecards.CommissionPayrollBurden — fully-loaded cost factor (payroll taxes)

// The DOS-guidance floor ladder — mirrors GetDosGuidanceFloor in SalesInvoiceOverrideService.cs.
// The at-sale SSP is multiplied by this, by how long the unit had sat on the shelf at the sale.
const DOS_LADDER = [
  { band: '0–60 days',    mult: 1.00, disc: 0,  name: 'Hold' },
  { band: '61–120 days',  mult: 0.95, disc: 5,  name: 'Patience' },
  { band: '121–180 days', mult: 0.90, disc: 10, name: 'Deeper Look' },
  { band: '181–270 days', mult: 0.88, disc: 12, name: 'Last Chance' },
  { band: '271+ days',    mult: 0.85, disc: 15, name: 'Manager Guidance' },
];
// Price-expiry limits — the at-sale SSP is only a valid basis if it hadn't gone stale by the sale date.
const PRICE_EXPIRY = { A: 90, B: 180 };

const fmt$ = (n) => (n == null ? '—' : '$' + Math.round(n).toLocaleString());
const fmtPct = (n) => (n == null ? '—' : (n * 100).toFixed(n * 100 % 1 === 0 ? 0 : 1) + '%');
const fmtMult = (n) => (n == null ? '—' : n.toFixed(2) + '×');

function Section({ id, title, children }) {
  return (
    <div id={id} style={{ scrollMarginTop: 90 }}>
      <Title order={2} mt={40} mb="sm">{title}</Title>
      {children}
    </div>
  );
}

// A small "who should care" tag used to orient the three audiences through the document.
function Audience({ children }) {
  return (
    <Group gap={6} mt={6} mb={2}>
      {children.split(',').map((a) => (
        <Badge key={a} size="xs" variant="light" color="gray" radius="sm">{a.trim()}</Badge>
      ))}
    </Group>
  );
}

const TOC = [
  { id: 'who-for', label: 'Who this is for' },
  { id: 'big-picture', label: 'The big picture (one paragraph)' },
  { id: 'two-numbers', label: 'The two numbers that decide a bonus' },
  { id: 'split-revenue', label: '— Split revenue (what counts as “yours”)' },
  { id: 'svsp', label: '— Price variance (Sales vs Sell Price)' },
  { id: 'guidance', label: '— How the DOS guidance is applied' },
  { id: 'plans', label: 'The plans: Manager vs Rep' },
  { id: 'how-built', label: 'How a bonus is built, step by step' },
  { id: 'baseline', label: '— Step 1 · Baseline bonus' },
  { id: 'revenue-multiplier', label: '— Step 2 · Revenue payout multiplier' },
  { id: 'variance-multiplier', label: '— Step 3 · Variance payout multiplier' },
  { id: 'worked-example', label: '— A full worked example' },
  { id: 'holdback', label: 'Quarters & the 25% holdback' },
  { id: 'true-up', label: 'The year-end true-up (payout or clawback)' },
  { id: 'accrual', label: 'The accrual: what the team costs' },
  { id: 'roles', label: 'Who can see what' },
  { id: 'where-numbers', label: 'Where the numbers come from' },
  { id: 'reading-screen', label: 'Reading the commission screens' },
];

export default function CommissionDocumentation() {
  const location = useLocation();
  const { data } = useAuthenticatedData(`${config.apiBaseUrl}/sales/commission/payout-tables?compYear=${COMP_YEAR}`);

  const rows = Array.isArray(data) ? data : [];
  const revTable = rows.filter((t) => t.tableType === 'Revenue').sort((a, b) => a.thresholdMin - b.thresholdMin);
  const varTable = rows.filter((t) => t.tableType === 'Variance').sort((a, b) => a.thresholdMin - b.thresholdMin);
  const rev = revTable.length ? revTable : REV_DEFAULT;
  const vr = varTable.length ? varTable : VAR_DEFAULT;

  useEffect(() => {
    const id = (location.hash || '').replace('#', '');
    if (!id) { window.scrollTo({ top: 0 }); return; }
    const t = setTimeout(() => {
      document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 60);
    return () => clearTimeout(t);
  }, [location.hash]);

  const jump = (id) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });

  return (
    <Stack gap="md" p="md" style={{ maxWidth: 900 }}>
      <div>
        <DraftBadge mb={6} />
        <Title order={1}>Sales Commissions — Documentation</Title>
        <Text size="sm" c="dimmed">
          How a salesperson&rsquo;s quarterly and annual bonus is worked out — from the revenue they&rsquo;re
          credited and how well they held price, through the plan schedules, the quarterly holdback, and the
          year-end true-up. Written for business users — no code, plain language. Every number below is a
          business rule, not a formula you have to trust: they are the same rules the screens run.
        </Text>
      </div>

      <Paper withBorder radius="md" p="md">
        <Text fw={600} size="sm" mb={6}>Contents</Text>
        <Stack gap={2}>
          {TOC.map((t) => (
            <Anchor key={t.id} href={`#${t.id}`} size="sm"
              onClick={(e) => { e.preventDefault(); window.history.replaceState(null, '', `#${t.id}`); jump(t.id); }}
              style={{ paddingLeft: t.label.startsWith('—') ? 16 : 0 }}>
              {t.label}
            </Anchor>
          ))}
        </Stack>
      </Paper>

      {/* ── Who this is for ── */}
      <Section id="who-for" title="Who this is for">
        <Text size="sm">
          The same commission runs through three sets of eyes. This document is written for all three, and
          each section notes who it matters most to.
        </Text>
        <SimpleGrid cols={{ base: 1, sm: 3 }} mt="md">
          <Paper withBorder radius="md" p="md">
            <Group gap="xs" mb={4}><ThemeIcon size="sm" radius="sm" color="grape">◆</ThemeIcon><Text fw={700} size="sm">Executive</Text></Group>
            <Text size="sm">
              Wants the <b>total cost</b> of the plan and its <b>shape</b>: are we paying for revenue we actually
              booked, is the plan self-funding, what will the year-end reconciliation add. Start at{' '}
              <a href="#big-picture">the big picture</a> and <a href="#accrual">the accrual</a>.
            </Text>
          </Paper>
          <Paper withBorder radius="md" p="md">
            <Group gap="xs" mb={4}><ThemeIcon size="sm" radius="sm" color="blue">◈</ThemeIcon><Text fw={700} size="sm">Sales manager</Text></Group>
            <Text size="sm">
              Owns the numbers: reviews each rep&rsquo;s revenue and price variance (with the DOS guidance already
              applied) and steps in with a manual override only in exceptional cases. Wants to see <b>every rep</b>
              and know each figure is defensible. Read it all — especially <a href="#svsp">price variance</a> and{' '}
              <a href="#true-up">the true-up</a>.
            </Text>
          </Paper>
          <Paper withBorder radius="md" p="md">
            <Group gap="xs" mb={4}><ThemeIcon size="sm" radius="sm" color="teal">●</ThemeIcon><Text fw={700} size="sm">Salesperson</Text></Group>
            <Text size="sm">
              Wants one answer: <b>what will I be paid, and what moves it?</b> The honest short version — sell
              more, and don&rsquo;t give away price. Read <a href="#two-numbers">the two numbers</a> and{' '}
              <a href="#worked-example">the worked example</a>.
            </Text>
          </Paper>
        </SimpleGrid>
      </Section>

      <Divider my="sm" />

      {/* ── Big picture ── */}
      <Section id="big-picture" title="The big picture (one paragraph)">
        <Paper withBorder radius="md" p="md" style={{ borderLeft: '4px solid #7048e8' }}>
          <Text size="sm">
            A salesperson earns a bonus for the <b>revenue credited to them</b>, scaled by <b>two multipliers</b>:
            one for <b>how much of their target they hit</b>, and one for <b>how well they held price</b> instead
            of discounting. The bonus is figured <b>every quarter</b>, but only <b>75% is paid then</b> — the
            other <b>25% is held back</b> and settled at year end, when the whole year is re-scored in one pass
            and reconciled against what was already paid. That year-end step can pay more out, or claw money back.
          </Text>
        </Paper>
        <Text size="sm" mt="sm">
          Everything else here is just the detail behind that paragraph: what &ldquo;credited revenue&rdquo; means,
          how &ldquo;holding price&rdquo; is measured, what the two multipliers are, and why the year-end number is
          not simply the four quarters added up.
        </Text>
      </Section>

      {/* ── Two numbers ── */}
      <Section id="two-numbers" title="The two numbers that decide a bonus">
        <Text size="sm">
          A bonus rests on exactly two measured inputs. Get these two right and everything downstream is a lookup.
        </Text>
        <List size="sm" mt="xs" spacing={6}>
          <List.Item><b>Split revenue</b> — the sales revenue <i>credited to this rep</i> for the period.</List.Item>
          <List.Item><b>Price variance (SvSP)</b> — how the prices they sold at compared to our system sell price: did they hold the line, or discount?</List.Item>
        </List>
      </Section>

      <Section id="split-revenue" title="Split revenue — what counts as “yours”">
        <Audience>Salesperson, Sales manager</Audience>
        <Text size="sm">
          Not every dollar on an invoice belongs to one person. A deal can be <b>split</b> between two
          salespeople — most commonly <b>50/50</b> when two reps share the account and the work. Split revenue is
          the sum of a rep&rsquo;s <b>own share</b> of every qualifying invoice line in the period.
        </Text>
        <Text size="sm" mt="xs">
          &ldquo;Qualifying&rdquo; does real work here — a line only counts if it&rsquo;s genuine, commissionable
          sales: it counts <b>inventory sales lines</b> and excludes internal/inter-company accounts and
          zero-credit splits. Two reps sharing a $10,000 line each see <b>$5,000</b> of credited revenue, not
          $10,000.
        </Text>
        <Text size="sm" mt="xs" c="dimmed">
          Revenue is <b>realized</b> revenue — shipped and invoiced — not orders that may never ship. It is the
          same split logic the old month-end spreadsheet used, now computed directly so it runs in seconds instead
          of half an hour.
        </Text>
      </Section>

      <Section id="svsp" title="Price variance — Sales vs Sell Price (SvSP)">
        <Audience>Salesperson, Sales manager</Audience>
        <Text size="sm">
          Revenue alone rewards <i>volume</i>. Price variance rewards <i>discipline</i>: it measures, for each
          line, how the <b>price actually sold</b> compares to our <b>system sell price (SSP)</b> for that part
          and condition, then blends those line-level differences into one <b>revenue-weighted</b> number for the
          rep.
        </Text>
        <List size="sm" mt="xs" spacing={4}>
          <List.Item><b>Positive</b> variance — sold <i>above</i> SSP. Held the line, or beat it.</List.Item>
          <List.Item><b>Zero</b> — sold right at SSP.</List.Item>
          <List.Item><b>Negative</b> — <i>discounted below</i> SSP. The deeper the discount, the more negative.</List.Item>
        </List>
        <Text size="sm" mt="xs">
          Revenue-weighted means a big line moves the number more than a small one — a rep can&rsquo;t offset one
          heavily-discounted large deal with a handful of small full-price ones.
        </Text>

        <Paper withBorder radius="md" p="sm" mt="md" style={{ borderLeft: '4px solid #868e96' }}>
          <Text size="sm" fw={700}>Aged stock is meant to be discounted</Text>
          <Text size="sm" mt={4}>
            Much of what ITS sells is <b>aged inventory</b> that is <i>meant</i> to move at a discount — judging it
            against an old list price would punish a rep for doing the right thing. So price variance isn&rsquo;t
            measured against a raw current SSP: it&rsquo;s measured against the <b>DOS guidance</b> — the approved
            discount for how long the unit had sat on the shelf. That&rsquo;s the whole point of the feature, and
            it&rsquo;s worth its own section next.
          </Text>
        </Paper>
        <Text size="xs" c="dimmed" mt={8}>
          Only lines with a valid price basis to compare against contribute to the variance. Lines with no basis
          are left out of it rather than counted as zero.
        </Text>
      </Section>

      <Section id="guidance" title="How the DOS guidance is applied (aged stock)">
        <Audience>Sales manager, Executive</Audience>
        <Text size="sm">
          This is what the app is really for: the discount guidance is <b>applied automatically to every eligible
          sale, by default</b> — nobody clicks anything. It is the standing rule for how an aged-stock sale is
          judged, not a step someone runs. Three rules govern it, and all three matter.
        </Text>

        <Text fw={600} size="sm" mt="md">1 · The SSP for the condition, as of the time of sale</Text>
        <Text size="sm">
          The basis is the <b>system sell price for the exact condition sold</b> (AR, SV, OH, or New/NE) — and,
          critically, the SSP <b>as it stood on the date the sale was struck</b>, not today&rsquo;s price. Prices
          change; a sale is judged against the number that was actually in force when the deal was priced, resolved
          from the point-in-time price history. A rep is never re-scored because the list price moved after the fact.
        </Text>

        <Text fw={600} size="sm" mt="md">2 · Discounted by days-on-shelf (DOS)</Text>
        <Text size="sm">
          That at-sale SSP is then discounted by <b>how long the unit had physically sat in the warehouse</b> at
          the point of sale — older stock earns a deeper approved discount. The result is the guidance floor the
          line&rsquo;s price variance is measured against:
        </Text>
        <Paper withBorder radius="md" p="md" mt="sm" style={{ maxWidth: 520 }}>
          <Text fw={600} size="sm" mb="xs">DOS guidance ladder</Text>
          <Table size="xs">
            <Table.Thead><Table.Tr>
              <Table.Th>Days on shelf at sale</Table.Th>
              <Table.Th>Guidance</Table.Th>
              <Table.Th style={{ textAlign: 'right' }}>Approved discount</Table.Th>
              <Table.Th style={{ textAlign: 'right' }}>Floor = SSP ×</Table.Th>
            </Table.Tr></Table.Thead>
            <Table.Tbody>
              {DOS_LADDER.map((b) => (
                <Table.Tr key={b.band}>
                  <Table.Td><Text size="xs">{b.band}</Text></Table.Td>
                  <Table.Td><Text size="xs" c="dimmed">{b.name}</Text></Table.Td>
                  <Table.Td style={{ textAlign: 'right' }}><Text size="xs">{b.disc}%</Text></Table.Td>
                  <Table.Td style={{ textAlign: 'right' }}><Text size="xs">{b.mult.toFixed(2)}</Text></Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Paper>

        <Text fw={600} size="sm" mt="md">3 · Only if the SSP wasn&rsquo;t expired at the time of sale</Text>
        <Text size="sm">
          A list price is only a fair yardstick while it&rsquo;s current. Prices go <b>stale</b>, and a stale price
          is no basis to grade a sale against. So the guidance is applied <b>only when the SSP had not expired by
          the sale date</b>. Expiry depends on the part&rsquo;s class:
        </Text>
        <List size="sm" mt="xs" spacing={4}>
          <List.Item><b>Class A</b> parts — the SSP expires after <b>{PRICE_EXPIRY.A} days</b>.</List.Item>
          <List.Item><b>Class B</b> parts — after <b>{PRICE_EXPIRY.B} days</b>.</List.Item>
        </List>
        <Text size="sm" mt="xs">
          The age is measured <b>at the time of sale</b> — days from when that condition&rsquo;s price was last set
          to the sale date. If it exceeded the class limit (or can&rsquo;t be determined), the SSP was stale when
          the deal was done, so <b>no guidance discount is applied</b> — that line is graded against the at-sale
          SSP as-is, rather than a discount built on a price that was already out of date.
        </Text>

        <Paper withBorder radius="md" p="sm" mt="md" style={{ borderLeft: '4px solid #2f9e44' }}>
          <Text size="sm" fw={700}>How the guidance reaches the bonus — and the manager override</Text>
          <Text size="sm" mt={4}>
            The guidance floor <b>is</b> the line&rsquo;s effective price by default — computed on the fly, not
            stored. The <a href="#svsp">price-variance</a> calculation measures every line against it, so an
            on-guidance discount reads as <b>holding price</b>, not losing it, and flows straight into the
            rep&rsquo;s <a href="#variance-multiplier">variance multiplier</a> — for the invoices screen and the
            bonus alike, because both read the same computation.
          </Text>
          <Text size="sm" mt={6}>
            A manager can still set a <b>manual override</b> on a specific line, and that supersedes the computed
            floor. But that is the <b>exception</b> — a deliberate override of what the system already applied — not
            a routine step. There is no &ldquo;apply&rdquo; button any more; the adjustment is simply always there.
          </Text>
        </Paper>
      </Section>

      <Divider my="sm" />

      {/* ── Plans ── */}
      <Section id="plans" title="The plans: Account Manager vs Account Rep">
        <Audience>Executive, Sales manager</Audience>
        <Text size="sm">
          Every commissioned salesperson is assigned <b>one plan</b>. A plan sets two things: the{' '}
          <b>annual revenue target</b> (100% of plan) and the <b>base bonus</b> — the bonus earned at exactly
          100% of target, before the price-variance multiplier. There are two plans today:
        </Text>
        <Table mt="md" withTableBorder>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Plan</Table.Th>
              <Table.Th style={{ textAlign: 'right' }}>Annual target</Table.Th>
              <Table.Th style={{ textAlign: 'right' }}>Quarterly target</Table.Th>
              <Table.Th style={{ textAlign: 'right' }}>Base bonus (at 100%)</Table.Th>
              <Table.Th style={{ textAlign: 'right' }}>Holdback</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {PLANS.map((p) => (
              <Table.Tr key={p.name}>
                <Table.Td><Text size="sm" fw={600}>{p.name}</Text></Table.Td>
                <Table.Td style={{ textAlign: 'right' }}>{fmt$(p.target)}</Table.Td>
                <Table.Td style={{ textAlign: 'right' }}>{fmt$(p.target / 4)}</Table.Td>
                <Table.Td style={{ textAlign: 'right' }}>{fmt$(p.base)}</Table.Td>
                <Table.Td style={{ textAlign: 'right' }}>{fmtPct(p.hold)}</Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
        <Text size="sm" mt="md">
          These two numbers set the <b>bonus rate</b> — how many dollars of revenue earn one dollar of baseline
          bonus. It&rsquo;s simply <b>target ÷ base bonus</b>:
        </Text>
        <List size="sm" mt="xs" spacing={4}>
          {PLANS.map((p) => (
            <List.Item key={p.name}>
              <b>{p.name}</b>: {fmt$(p.target)} ÷ {fmt$(p.base)} = <b>{Math.round(p.target / p.base).toLocaleString()}</b> —
              about <b>{fmt$(p.target / p.base)}</b> of revenue per $1 of baseline bonus.
            </List.Item>
          ))}
        </List>
        <Text size="xs" c="dimmed" mt={8}>
          Each rep&rsquo;s plan is an <b>effective-dated assignment</b> (a rep-to-plan mapping with a start date),
          so a promotion mid-year is a new assignment rather than an edit to history. Today that mapping is
          maintained as configuration, not through a screen.
        </Text>

        <Paper withBorder radius="md" p="sm" mt="md" style={{ borderLeft: '4px solid #e8590c' }}>
          <Text size="sm" fw={700}>Who a commission is computed for</Text>
          <Text size="sm" mt={4}>
            A rep appears on the commission screens only when <b>both</b> are true: they are in the{' '}
            <b>&ldquo;Sales&rdquo; group</b>, <b>and</b> they have a plan assignment for the year. A rep in the
            Sales group with <b>no plan assignment is silently left out</b> of the team summary — not shown as
            zero, just absent. So &ldquo;missing&rdquo; usually means a plan assignment was never created for that
            person, and the fix is to add one.
          </Text>
        </Paper>
      </Section>

      <Divider my="sm" />

      {/* ── How built ── */}
      <Section id="how-built" title="How a bonus is built, step by step">
        <Text size="sm">
          For a given period (a quarter, or the full year), the bonus is built in three multiplications. The same
          three steps run for every rep and every period — only the inputs change.
        </Text>
        <Text size="xs" ff="monospace" mt={8} style={{ whiteSpace: 'pre', lineHeight: 1.6 }}>
{`  revenue ÷ bonus rate   =  baseline bonus     (Step 1)
       × revenue payout  =  adjusted bonus     (Step 2 — how much of plan you hit)
       × variance payout =  EARNED             (Step 3 — how well you held price)`}
        </Text>
      </Section>

      <Section id="baseline" title="Step 1 · Baseline bonus">
        <Text size="sm">
          Baseline bonus is just <b>credited revenue ÷ the bonus rate</b>. It scales straight with revenue, so a
          rep who books exactly their target earns exactly the <a href="#plans">base bonus</a> at this stage. Book
          half the target and the baseline is half the base bonus; book double and it&rsquo;s double. Nothing is
          curved yet — that&rsquo;s the next two steps.
        </Text>
      </Section>

      <Section id="revenue-multiplier" title="Step 2 · Revenue payout multiplier">
        <Text size="sm">
          The baseline is then multiplied by a rate that depends on <b>what percentage of the target the rep
          hit</b>. This is a <b>step schedule</b>, and it is deliberately <b>back-loaded</b>: hitting most of plan
          pays less than proportionally, hitting plan pays full, and beating plan pays an accelerating bonus.
          <b> Below 50% of plan, it pays nothing.</b>
        </Text>
        <Paper withBorder radius="md" p="md" mt="sm" style={{ maxWidth: 420 }}>
          <Text fw={600} size="sm" mb="xs">Revenue payout schedule</Text>
          <Table size="xs">
            <Table.Thead><Table.Tr>
              <Table.Th>% of plan reached</Table.Th>
              <Table.Th style={{ textAlign: 'right' }}>Multiplier</Table.Th>
            </Table.Tr></Table.Thead>
            <Table.Tbody>
              <Table.Tr style={{ background: 'var(--mantine-color-red-0)' }}>
                <Table.Td><Text size="xs" c="dimmed">below 50%</Text></Table.Td>
                <Table.Td style={{ textAlign: 'right' }}><Text size="xs" c="red">0× (no bonus)</Text></Table.Td>
              </Table.Tr>
              {rev.map((r) => (
                <Table.Tr key={r.thresholdMin}>
                  <Table.Td><Text size="xs">≥ {fmtPct(r.thresholdMin)}</Text></Table.Td>
                  <Table.Td style={{ textAlign: 'right' }}><Text size="xs">{fmtMult(r.payoutRate)}</Text></Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Paper>
        <Text size="sm" mt="sm">
          Read it as a floor: you earn the multiplier of the <b>highest row you&rsquo;ve reached</b>. Hit 88% of
          plan and you&rsquo;re on the <b>{fmtMult((rev.find((r) => r.thresholdMin === 0.85) || {}).payoutRate)}</b> row
          (the 85% step), not a pro-rated 88%. That gap is on purpose — it makes the last push to plan the most
          valuable selling a rep does all quarter.
        </Text>
      </Section>

      <Section id="variance-multiplier" title="Step 3 · Variance payout multiplier">
        <Text size="sm">
          The adjusted bonus is finally multiplied by a rate that depends on the rep&rsquo;s{' '}
          <a href="#svsp">price variance</a>. This is where discipline pays — or costs. Discount too hard on a
          revenue-weighted basis and it <b>zeroes the entire bonus for the period</b>; hold or beat price and it
          <b> adds up to 20%</b>.
        </Text>
        <Paper withBorder radius="md" p="md" mt="sm" style={{ maxWidth: 460 }}>
          <Text fw={600} size="sm" mb="xs">Variance payout schedule</Text>
          <Table size="xs">
            <Table.Thead><Table.Tr>
              <Table.Th>Price variance (SvSP)</Table.Th>
              <Table.Th style={{ textAlign: 'right' }}>Multiplier</Table.Th>
            </Table.Tr></Table.Thead>
            <Table.Tbody>
              <Table.Tr style={{ background: 'var(--mantine-color-red-0)' }}>
                <Table.Td><Text size="xs" c="dimmed">below -5%</Text></Table.Td>
                <Table.Td style={{ textAlign: 'right' }}><Text size="xs" c="red">0× (no bonus)</Text></Table.Td>
              </Table.Tr>
              {vr.map((r) => (
                <Table.Tr key={r.thresholdMin}>
                  <Table.Td><Text size="xs">≥ {fmtPct(r.thresholdMin)}</Text></Table.Td>
                  <Table.Td style={{ textAlign: 'right' }}><Text size="xs">{fmtMult(r.payoutRate)}</Text></Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Paper>
        <List size="sm" mt="sm" spacing={4}>
          <List.Item>Give away more than <b>5%</b> below sell price (weighted) → <b>bonus is 0</b> for the period.</List.Item>
          <List.Item>Between <b>-5% and -2%</b> → the bonus is <b>halved</b>.</List.Item>
          <List.Item>Within <b>±2%</b> of sell price → <b>no penalty, no bonus</b> (the neutral band).</List.Item>
          <List.Item>Above <b>+2%</b> and <b>+5%</b> → <b>+10%</b> then <b>+20%</b>.</List.Item>
        </List>
        <Paper withBorder radius="md" p="sm" mt="sm" style={{ borderLeft: '4px solid #2f9e44' }}>
          <Text size="sm">
            <b>If there&rsquo;s no price data to judge, this multiplier is 1.00× — neutral.</b> A rep is never
            penalized for lines we couldn&rsquo;t price-check. Variance only ever moves the bonus when we actually
            know what the line should have sold for.
          </Text>
        </Paper>
      </Section>

      <Section id="worked-example" title="A full worked example">
        <Audience>Salesperson, Sales manager</Audience>
        <Text size="sm">
          One quarter for an <b>Account Manager</b> (annual target {fmt$(5000000)}, base bonus {fmt$(25000)}, so a
          bonus rate of <b>200</b> and a quarterly target of {fmt$(1250000)}):
        </Text>
        <Paper withBorder radius="md" p="md" mt="sm">
          <Text size="xs" fw={700} tt="uppercase" c="dimmed" mb={6}>The quarter</Text>
          <Text size="sm">Credited split revenue <b>{fmt$(1250000)}</b> · price variance <b>+3%</b> (held price well).</Text>
          <List size="sm" mt={8} spacing={4}>
            <List.Item><b>Step 1 — baseline:</b> {fmt$(1250000)} ÷ 200 = <b>{fmt$(6250)}</b>.</List.Item>
            <List.Item><b>% of plan:</b> {fmt$(1250000)} ÷ {fmt$(1250000)} = <b>100%</b> → revenue multiplier <b>1.00×</b>.</List.Item>
            <List.Item><b>Step 2 — adjusted bonus:</b> {fmt$(6250)} × 1.00 = <b>{fmt$(6250)}</b>.</List.Item>
            <List.Item><b>Variance +3%</b> → lands on the ≥ +2% row → variance multiplier <b>1.10×</b>.</List.Item>
            <List.Item><b>Step 3 — earned:</b> {fmt$(6250)} × 1.10 = <b>{fmt$(6875)}</b>.</List.Item>
            <List.Item><b>Holdback (25%):</b> {fmt$(6875)} × 0.25 = <b>{fmt$(1719)}</b> held.</List.Item>
            <List.Item><b>Paid this quarter:</b> {fmt$(6875)} − {fmt$(1719)} = <b>{fmt$(5156)}</b>.</List.Item>
          </List>
        </Paper>
        <Text size="sm" mt="md">
          Now change one thing — the <b>same revenue, but a 6% discount</b> (variance <b>-6%</b>). That falls below
          the -5% line, so the variance multiplier is <b>0×</b>: earned <b>{fmt$(0)}</b>, paid <b>{fmt$(0)}</b>.
          Same sales, no bonus — because the price was given away. That single row is the whole point of the plan.
        </Text>
      </Section>

      <Divider my="sm" />

      {/* ── Holdback ── */}
      <Section id="holdback" title="Quarters & the 25% holdback">
        <Audience>Executive, Salesperson</Audience>
        <Text size="sm">
          The bonus is calculated and paid <b>every quarter</b>, so reps are rewarded through the year rather than
          waiting for December. But each quarter only pays <b>75% of what was earned</b>; the remaining <b>25% is
          held back</b>.
        </Text>
        <Text size="sm" mt="xs">
          The holdback exists because quarters are lumpy and the schedule is non-linear. A rep can have a huge Q1
          and a weak Q4; paying every quarter at 100% would over-pay early against a year that ends below plan. The
          25% cushion is what the <a href="#true-up">year-end true-up</a> settles against — releasing it when the
          year holds up, or absorbing an over-payment when it doesn&rsquo;t.
        </Text>
      </Section>

      <Section id="true-up" title="The year-end true-up (payout or clawback)">
        <Audience>Executive, Sales manager</Audience>
        <Text size="sm">
          At year end the bonus is computed <b>one more time, over the whole year at once</b>: annual revenue
          against the <b>annual</b> target, and a single <b>revenue-weighted variance</b> across all four quarters.
          That produces the rep&rsquo;s <b>true full-year earned</b>. From it we subtract everything already paid
          across the quarters. The difference is the <b>year-end payout</b> — or, if the quarters over-paid, a{' '}
          <b>clawback</b>.
        </Text>
        <Paper withBorder radius="md" p="sm" mt="sm" style={{ borderLeft: '4px solid #e8590c' }}>
          <Text size="sm" fw={700}>Why the full year isn&rsquo;t just the four quarters added up</Text>
          <Text size="sm" mt={4}>
            Because both multipliers are <b>step functions</b>, and steps don&rsquo;t add. A rep at 60% in three
            quarters and 130% in one is not &ldquo;averaging 77%&rdquo; to the schedule — measured quarter by
            quarter they sat on low multipliers three times, but measured over the <i>whole year</i> their total
            revenue may clear a much higher step. The annual pass re-scores them as one continuous year, and the
            true-up is the correction. It&rsquo;s also what finally releases the four quarterly holdbacks.
          </Text>
        </Paper>
        <Text size="xs" c="dimmed" mt={8}>
          On the screens the full-year column shows this true-up as <b>&ldquo;EOY Payout / Clawback&rdquo;</b> — and
          it goes red when it&rsquo;s a clawback.
        </Text>
      </Section>

      <Divider my="sm" />

      {/* ── Accrual ── */}
      <Section id="accrual" title="The accrual: what the team costs">
        <Audience>Executive</Audience>
        <Text size="sm">
          Every calculated bonus is also a <b>cost to the company</b>. The plan is designed so leadership can track
          the running total — the <b>accrual</b> — and never be surprised by the year-end liability. Two figures
          matter:
        </Text>
        <List size="sm" mt="xs" spacing={4}>
          <List.Item><b>Earned to date</b> — the bonus dollars accrued across the team so far this year (paid plus held-back).</List.Item>
          <List.Item>
            <b>Fully-loaded cost</b> — earned × the <b>payroll burden</b> factor of <b>{BURDEN}×</b>, which adds
            employer payroll taxes. A {fmt$(100000)} bonus pool actually costs about {fmt$(100000 * BURDEN)}.
          </List.Item>
        </List>
        <Text size="sm" mt="xs" c="dimmed">
          The burden factor is config, effective-dated, so a change in payroll-tax rates is a new entry rather than
          a rewrite. Each calculation is also snapshotted per rep and period, which is what a team-wide accrual
          report draws on.
        </Text>
        <Paper withBorder radius="md" p="sm" mt="sm" style={{ borderLeft: '4px solid var(--mantine-color-gray-5)' }}>
          <Text size="xs" c="dimmed">
            The building blocks — the burden factor and the per-run snapshots — exist today. A dedicated accrual
            screen is planned and not yet built; until then this is the intended cost view rather than a live page.
          </Text>
        </Paper>
      </Section>

      {/* ── Roles ── */}
      <Section id="roles" title="Who can see what">
        <Audience>Executive, Sales manager, Salesperson</Audience>
        <Text size="sm">
          Commission numbers are <b>confidential</b>. A salesperson must not be able to see what a peer is paid.
          Visibility is designed around that rule:
        </Text>
        <List size="sm" mt="xs" spacing={6}>
          <List.Item>
            <b>Salesperson</b> — sees <b>only their own</b> dashboard: their revenue, variance, schedules, and
            payout. They cannot open another rep&rsquo;s figures or the team roll-up.
          </List.Item>
          <List.Item>
            <b>Sales manager</b> — sees the <b>whole team</b>: every rep side by side, can open any rep&rsquo;s
            detail, and set a manual override on a line in the exceptional case.
          </List.Item>
          <List.Item>
            <b>Executive</b> — cares about the <b>totals and overall cost</b> — the plan&rsquo;s shape and
            liability (see <a href="#accrual">the accrual</a>) — rather than the individual-rep grain.
          </List.Item>
        </List>
        <Text size="xs" c="dimmed" mt={8}>
          That is the <b>go-live</b> model. <b>Right now, in the pilot, it is not yet enforced</b>: the screens are
          manager-review only — anyone who can reach them (the menu is limited to a small Experimental pilot group)
          sees every rep. Per-salesperson self-scoping is deferred until the feature comes online, when the
          reviewer/salesperson roles are finalized.
        </Text>
      </Section>

      {/* ── Where numbers come from ── */}
      <Section id="where-numbers" title="Where the numbers come from">
        <Audience>Sales manager, Executive</Audience>
        <Text size="sm">
          The revenue and variance figures are read from the <b>data warehouse</b>, not typed in and not pulled
          live from the ERP. Invoices flow from the ERP into the warehouse on a regular refresh (roughly hourly),
          and the commission screens read from there — so figures update as the warehouse refreshes, not the
          instant an invoice is keyed.
        </Text>
        <List size="sm" mt="xs" spacing={4}>
          <List.Item><b>Split revenue</b> and <b>price variance</b> are each a single warehouse query per rep per period, applying the split and eligibility rules described above.</List.Item>
          <List.Item>The <b>DOS-guided effective price</b> (and the variance from it) is computed in <b>one shared place</b> — a single warehouse view both the invoices screen and the bonus read — so the two can never disagree. A <b>manual override</b>, when a manager sets one, is stored and supersedes the computed default for that line.</List.Item>
          <List.Item>The <b>plans, schedules, periods, and burden</b> are all configuration tables — changing a payout row or a target changes every calculation and this document, without a code change.</List.Item>
        </List>
        <Text size="xs" c="dimmed" mt={8}>
          This replaces the old half-hour stored-procedure export into Excel: the same business logic, extracted so
          it runs in seconds and shows the same answer on screen for everyone entitled to see it.
        </Text>
      </Section>

      {/* ── Reading the screens ── */}
      <Section id="reading-screen" title="Reading the commission screens">
        <Text size="sm">There are two screens, and they share the same vocabulary as this document.</Text>
        <Text fw={600} size="sm" mt="md">Team Commissions (managers)</Text>
        <Text size="sm">
          One row per rep, with a <b>Q1–Q4 / FY</b> switch at the top. Each row shows revenue, target, % of plan,
          variance (with the DOS guidance already applied), earned, and paid (with the amount held). Click a row to
          open that rep&rsquo;s full dashboard, or the adjust icon to see that rep&rsquo;s invoice lines.
        </Text>
        <Text fw={600} size="sm" mt="md">Rep bonus dashboard</Text>
        <Text size="sm">
          One rep, all four quarters plus a full-year column. The rows are exactly the three build steps:{' '}
          <b>Baseline Bonus → Rev Payout → Adj Bonus → Var Payout → Earned</b>, then <b>Holdback</b> and{' '}
          <b>Paid</b>. The three boxes beneath total the quarterly pay, the year-end payout/clawback, and the
          final total. The two reference tables are the live <a href="#revenue-multiplier">revenue</a> and{' '}
          <a href="#variance-multiplier">variance</a> schedules, with the rep&rsquo;s current row highlighted.
        </Text>
        <Group mt="md" gap="xs">
          <ThemeIcon variant="light" color="gray" size="sm" radius="xl">i</ThemeIcon>
          <Text size="xs" c="dimmed">
            Every figure on both screens is one of the rules above applied to warehouse data — nothing on the
            screen is hand-entered, so what you read is reproducible by hand from this document.
          </Text>
        </Group>
      </Section>
    </Stack>
  );
}
